
import React, { useState } from 'react';
import { CurriculumData, SubjectType, CurriculumNode, Language, UserProfile } from '../types';
import { generateCurriculumExpansion } from '../services/geminiService';
import { CheckCircle, Lock, Plus, Loader2, LayoutList, Network } from 'lucide-react';
import { translations } from '../i18n';
import { GalaxyMap } from './GalaxyMap';

interface AcademyWithUserProps {
  curriculum: CurriculumData;
  unlockedSkills: string[];
  activeSubject: SubjectType;
  lang: Language;
  onSubjectChange: (s: SubjectType) => void;
  onNodeSelect: (node: CurriculumNode) => void;
  onUpdateCurriculum: (c: CurriculumData) => void;
  user: UserProfile; 
}

const SUBJECT_THEMES: Record<SubjectType, { bg: string, text: string, border: string, gradient: string, shadow: string, icon: string }> = {
  math: { 
    bg: 'bg-blue-600', text: 'text-blue-600', border: 'border-blue-600', 
    gradient: 'from-blue-600 to-blue-700', shadow: 'shadow-blue-500/40', icon: 'bg-blue-600' 
  },
  science: { 
    bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-600', 
    gradient: 'from-emerald-600 to-teal-600', shadow: 'shadow-emerald-500/40', icon: 'bg-emerald-600' 
  },
  chinese: { 
    bg: 'bg-red-600', text: 'text-red-600', border: 'border-red-600', 
    gradient: 'from-red-600 to-rose-600', shadow: 'shadow-red-500/40', icon: 'bg-red-600' 
  },
  english: { 
    bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-600', 
    gradient: 'from-violet-600 to-purple-600', shadow: 'shadow-violet-500/40', icon: 'bg-violet-600' 
  },
  history: { 
    bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-600', 
    gradient: 'from-amber-600 to-orange-600', shadow: 'shadow-amber-500/40', icon: 'bg-amber-600' 
  },
  art: { 
    bg: 'bg-pink-600', text: 'text-pink-600', border: 'border-pink-600', 
    gradient: 'from-pink-600 to-rose-600', shadow: 'shadow-pink-500/40', icon: 'bg-pink-600' 
  }
};

export const Academy: React.FC<AcademyWithUserProps> = ({ 
  curriculum, unlockedSkills, activeSubject, lang, onSubjectChange, onNodeSelect, onUpdateCurriculum, user
}) => {
  const [expanding, setExpanding] = React.useState(false);
  // View Mode: 'list' | 'galaxy'
  const [viewMode, setViewMode] = useState<'list' | 'galaxy'>('galaxy'); // Default to galaxy for "Wow" factor

  const t = translations[lang];

  const handleExpand = async () => {
    setExpanding(true);
    const newNodes = await generateCurriculumExpansion(
      activeSubject, 
      curriculum[activeSubject].nodes, 
      user, 
      lang
    );
    
    if (newNodes.length > 0) {
      const updated = {
        ...curriculum,
        [activeSubject]: {
          ...curriculum[activeSubject],
          nodes: [...curriculum[activeSubject].nodes, ...newNodes]
        }
      };
      onUpdateCurriculum(updated);
    }
    setExpanding(false);
  };

  const subjectData = curriculum[activeSubject];
  const theme = SUBJECT_THEMES[activeSubject];
  const lastNode = subjectData.nodes[subjectData.nodes.length - 1];
  const canExpand = unlockedSkills.includes(lastNode.id);

  return (
    <div className="pb-20">
      {/* 1. Sticky Header Bar: Subjects + View Toggle */}
      <div className="sticky top-[80px] z-30 mb-6 flex flex-col md:flex-row items-start md:items-center gap-4 py-2 px-1">
          
          {/* Subject Tabs - OPTIMIZED FOR CONTRAST */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 max-w-full pb-2 md:pb-0">
            {Object.entries(SUBJECT_THEMES).map(([key, style]) => {
              const isActive = activeSubject === key;
              return (
                <button
                  key={key}
                  onClick={() => onSubjectChange(key as SubjectType)}
                  className={`
                    px-5 py-2.5 rounded-full font-bold text-sm whitespace-nowrap transition-all duration-300 flex items-center gap-2 border shadow-sm
                    ${isActive 
                      ? `${style.bg} text-white border-transparent shadow-lg scale-105` 
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}
                  `}
                >
                  {t.subjects[key as SubjectType]}
                </button>
              );
            })}
          </div>

          {/* View Toggle */}
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md p-1 rounded-xl border border-slate-200 dark:border-white/10 flex shrink-0 self-end md:self-auto shadow-sm">
             <button 
               onClick={() => setViewMode('galaxy')}
               className={`p-2 rounded-lg transition-all ${viewMode === 'galaxy' ? 'bg-indigo-500 text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
               title="Galaxy Map"
             >
               <Network size={20} />
             </button>
             <button 
               onClick={() => setViewMode('list')}
               className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-indigo-500 text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
               title="List View"
             >
               <LayoutList size={20} />
             </button>
          </div>
      </div>

      <div className="bg-white/50 dark:bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] p-6 md:p-8 border border-white/40 dark:border-white/10 shadow-xl relative overflow-hidden min-h-[600px]">
        {/* Header Background */}
        <div className={`absolute top-0 left-0 right-0 h-80 opacity-10 dark:opacity-20 bg-gradient-to-b ${theme.gradient} to-transparent pointer-events-none transition-all duration-500`}></div>
        
        {/* Title Area */}
        <div className="relative z-10 mb-8 text-center animate-in fade-in slide-in-from-top-4 duration-500">
           <h2 className={`text-3xl font-black mb-2 font-orbitron ${theme.text} dark:text-white`}>{subjectData.title}</h2>
           <p className="text-slate-600 dark:text-indigo-200/80 font-bold max-w-md mx-auto">{subjectData.desc}</p>
        </div>

        {/* --- VIEW MODE: GALAXY MAP --- */}
        {viewMode === 'galaxy' && (
           <div className="animate-in zoom-in-95 duration-500">
              <GalaxyMap 
                nodes={subjectData.nodes}
                unlockedSkills={unlockedSkills}
                theme={theme}
                onNodeSelect={onNodeSelect}
                subjectTitle={subjectData.title}
              />
              
              {/* Expand Controls for Map Mode */}
              <div className="mt-8 flex justify-center">
                 <button 
                    disabled={!canExpand || expanding}
                    onClick={handleExpand}
                    className={`px-8 py-4 rounded-full font-bold transition-all flex items-center gap-3 border shadow-lg ${
                      canExpand 
                        ? 'bg-slate-800 dark:bg-slate-700 border-transparent text-white hover:bg-slate-700 hover:scale-105' 
                        : 'bg-slate-200 dark:bg-slate-800/50 border-slate-300 dark:border-white/5 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {expanding ? <Loader2 className="animate-spin" size={20}/> : <Plus size={20}/>}
                    {expanding ? t.generatingPath : canExpand ? t.expand : t.completePrev}
                  </button>
              </div>
           </div>
        )}

        {/* --- VIEW MODE: LIST TREE --- */}
        {viewMode === 'list' && (
          <div className="relative max-w-lg mx-auto pb-10 animate-in fade-in slide-in-from-bottom-8 duration-500">
            {/* Connector Line */}
            <div className={`absolute left-[28px] top-8 bottom-10 w-1 rounded-full z-0 opacity-20 ${theme.bg}`}></div>
            
            <div className="space-y-8 relative z-10">
              {subjectData.nodes.map((node, i) => {
                const isCompleted = unlockedSkills.includes(node.id);
                const isUnlocked = node.prev === null || unlockedSkills.includes(node.prev);
                const isLocked = !isUnlocked;
                
                return (
                  <div 
                    key={node.id}
                    className={`group relative flex items-start gap-6 transition-all duration-300 ${isLocked ? 'opacity-60 grayscale blur-[0.5px] hover:blur-0' : 'opacity-100'}`}
                  >
                    {/* Node Icon */}
                    <div 
                      onClick={() => isUnlocked && onNodeSelect(node)}
                      className={`
                        w-14 h-14 rounded-full flex items-center justify-center shrink-0 border-[4px] relative z-20 transition-all cursor-pointer shadow-lg
                        ${isCompleted 
                          ? 'bg-emerald-500 border-emerald-200 dark:border-emerald-900 text-white hover:scale-110' 
                          : isUnlocked 
                            ? `${theme.bg} border-white dark:border-slate-800 text-white hover:scale-110` 
                            : 'bg-slate-200 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-400 cursor-not-allowed'}
                      `}
                    >
                      {isCompleted ? <CheckCircle size={24} strokeWidth={3}/> : isUnlocked ? <span className="text-lg font-black">{i + 1}</span> : <Lock size={20}/>}
                    </div>

                    {/* Node Card */}
                    <div 
                      onClick={() => isUnlocked && onNodeSelect(node)}
                      className={`
                        flex-1 p-5 rounded-2xl border transition-all cursor-pointer relative top-1
                        ${isCompleted 
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30' 
                          : isUnlocked 
                            ? 'bg-white dark:bg-slate-800 border-slate-100 dark:border-white/10 shadow-lg hover:border-indigo-300 dark:hover:border-indigo-500 hover:-translate-y-1' 
                            : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-white/5 cursor-not-allowed'}
                      `}
                    >
                       <div className="flex justify-between items-start">
                         <h4 className={`font-black text-lg mb-1 ${isUnlocked ? 'text-slate-800 dark:text-white' : 'text-slate-500 dark:text-slate-500'}`}>{node.title}</h4>
                         {isUnlocked && !isCompleted && (
                           <span className={`${theme.bg} text-white text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider animate-pulse shadow-md`}>{t.startBtn}</span>
                         )}
                       </div>
                       <p className={`text-sm font-medium leading-relaxed ${isUnlocked ? 'text-slate-600 dark:text-indigo-200/60' : 'text-slate-400 dark:text-slate-600'}`}>{node.desc}</p>
                    </div>
                  </div>
                );
              })}

              {/* Expand Button */}
              <div className="relative flex items-center gap-6 pt-4">
                <div className="w-14 flex justify-center">
                   <div className={`w-4 h-4 rounded-full ${canExpand ? `${theme.bg} animate-ping` : 'bg-slate-300 dark:bg-slate-700'}`}></div>
                </div>
                <button 
                  disabled={!canExpand || expanding}
                  onClick={handleExpand}
                  className={`flex-1 py-4 rounded-2xl border-2 border-dashed font-bold transition-all flex items-center justify-center gap-2 ${
                    canExpand 
                      ? 'border-indigo-300 dark:border-white/20 text-indigo-600 dark:text-indigo-200 bg-indigo-50 dark:bg-white/5 hover:border-indigo-500 hover:bg-indigo-100 dark:hover:bg-white/10' 
                      : 'border-slate-200 dark:border-white/10 text-slate-400 bg-slate-50 dark:bg-white/5'
                  }`}
                >
                   {expanding ? <Loader2 className="animate-spin" size={20}/> : <Plus size={20}/>}
                   {expanding ? t.generatingPath : canExpand ? t.expand : t.completePrev}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
